import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import { requests, send, type Me } from "../api/auth";
import { resolveApiBase } from "../api/base";
import type { LoadState } from "./access";
import { browserStore, forgetAll, isWorkerPath, recall, remember } from "./lastKnown";

// ---------------------------------------------------------------------------
// One /v1/me for the whole application.
//
// TWO THINGS HERE ARE DELIBERATE AND BOTH COME FROM V3's DESIGN:
//
//   1. `authenticated: false` IS A SUCCESSFUL ANSWER, NOT AN ERROR. /v1/me
//      returns 200 with an empty payload for a signed-out visitor, because it
//      is the first thing the SPA asks on a cold load. Treating that as a
//      failure would make every anonymous page view an error in the console and
//      would make a real outage indistinguishable from being logged out.
//
//   2. THE SESSION TOKEN IS NEVER TOUCHED. It lives in a __Host- HttpOnly
//      cookie the browser attaches itself; script cannot read it, which is what
//      stops an XSS in this origin from exfiltrating a session. There is
//      nothing to store here and storing anything would be undoing that.
//
//   3. S18. WHAT /v1/me LAST SAID IS KEPT, NEVER THE TOKEN. On the worker surface a
//      network failure (not a refusal) renders the last-known `me`, flagged by
//      `offlineAsOf`, so a worker with no signal still sees where to stand. Being
//      told "not signed in" forgets it, and so does signing out (session/lastKnown.ts).
// ---------------------------------------------------------------------------

export type Session = {
  state: LoadState;
  me: Me | null;
  apiBase: string;
  /** S18. When the worker surface is showing what it last knew, the instant it knew it. */
  offlineAsOf: string | null;
  /**
   * Re-ask. Every privilege change issues a new session, so callers must.
   *
   * Returns what it read, so a caller that needs to act on the new session —
   * "sign in, then go to the right surface" — does not have to fetch /v1/me a
   * second time to see a value it cannot read out of state synchronously.
   */
  reload: () => Promise<Me | null>;
};

const SessionContext = createContext<Session | null>(null);

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const apiBase = useMemo(
    () => resolveApiBase(import.meta.env.VITE_API_BASE as string | undefined, window.location.origin),
    [],
  );
  const [state, setState] = useState<LoadState>("loading");
  const [me, setMe] = useState<Me | null>(null);
  const [offlineAsOf, setOfflineAsOf] = useState<string | null>(null);

  // StrictMode mounts effects twice in development. Without this, a cold load
  // fires two /v1/me requests and the second can land first — which is
  // harmless here but is the shape of a bug that is not harmless later, and it
  // makes the network panel lie about what the app does.
  const generation = useRef(0);

  const reload = useCallback(async (): Promise<Me | null> => {
    const mine = (generation.current += 1);
    // S18. No answer at all: the worker surface shows what it last knew, and every other
    // surface keeps the gate. Nothing here can make a signed-out person look signed in,
    // because only an authenticated 200 is ever remembered and a signed-out 200 forgets.
    const unreachable = (): Me | null => {
      const last = isWorkerPath(window.location.pathname) ? recall<Me>(browserStore(), "me") : null;
      if (last !== null && last.value.authenticated) {
        setMe(last.value);
        setOfflineAsOf(last.savedAt);
        setState("ready");
        return last.value;
      }
      setMe(null);
      setOfflineAsOf(null);
      setState("error");
      return null;
    };
    try {
      const { status, body } = await send<Me>(requests.me(apiBase));
      if (mine !== generation.current) return null;
      if (status !== 200) {
        // A non-200 from /v1/me means the API is unwell, NOT that this person
        // is signed out — being signed out is a 200. Keeping the two apart is
        // what stops a network blip from teaching people to retype a password
        // whenever the page misbehaves, which is a phishing lesson delivered by
        // their own employer.
        return unreachable();
      }
      const kv = browserStore();
      if (body.authenticated) remember(kv, "me", body.user.id, body);
      else forgetAll(kv);
      setMe(body);
      setOfflineAsOf(null);
      setState("ready");
      return body;
    } catch {
      if (mine !== generation.current) return null;
      return unreachable();
    }
  }, [apiBase]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const value = useMemo<Session>(
    () => ({ state, me, apiBase, offlineAsOf, reload }),
    [state, me, apiBase, offlineAsOf, reload],
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): Session {
  const value = useContext(SessionContext);
  if (!value) throw new Error("useSession was called outside SessionProvider");
  return value;
}
